import type { ReactElement } from "react";
import {
  Datagrid,
  FunctionField,
  ReferenceArrayField,
  ReferenceField,
  TextField,
  useGetMany,
  useListContext,
  useRecordContext,
} from "react-admin";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { DateField } from "../../fields/DateField";
import { CommaSeparatedFieldList } from "../../misc/CommaSeparatedFieldList";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Column } from "../../misc/Column";
import { grants } from "../../backoffice.access_control";
import type { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import type { Instructor } from "../../providers/instructorsProvider";
import type { Car, Vehicle } from "../../providers/vehiclesProvider";
import { getEntitledInstructorIds, VehicleMobileListItem } from "./VehicleMobileListItem";
import { formatGearType } from "./vehicleFormatters";

export type VehicleListResource = "cars" | "motorcycles" | "trailers" | "simulators" | "trucks" | "buses" | "tractors";
export type VehicleListVariant = "active" | "deleted";

export function VehiclesDataGrid(props: {
  resource: VehicleListResource;
  empty?: ReactElement;
  variant?: VehicleListVariant;
}) {
  const { resource, empty, variant = "active" } = props;
  const isDeleted = variant === "deleted";
  const { isLoading, data, total } = useListContext<Vehicle>();
  const drivingSchool = useRecordContext<DrivingSchool>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const rowClick = _getRowClick(resource, variant);
  const entitledInstructorIds =
    isMobile && variant === "active" && resource !== "simulators" && data
      ? Array.from(new Set(data.flatMap(getEntitledInstructorIds)))
      : [];
  const { data: instructors = [] } = useGetMany<Instructor>(
    "instructors",
    { ids: entitledInstructorIds },
    { enabled: entitledInstructorIds.length > 0 },
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    const instructorNamesById = Object.fromEntries(instructors.map((instructor) => [instructor.id, instructor.name]));

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((vehicle) => (
          <VehicleMobileListItem
            key={vehicle.id}
            vehicle={vehicle}
            drivingSchool={drivingSchool}
            instructorNamesById={instructorNamesById}
            variant={variant}
            onClick={rowClick === false ? undefined : () => navigate(`/${resource}/${vehicle.id}`)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid bulkActionButtons={false} rowClick={rowClick} empty={empty}>
      <TextField label="Name" source="name" />
      <TextField label="Notizen" source="notes" />
      {resource !== "simulators" && <TextField label="Kennzeichen" source="plateNumber" />}
      {resource === "cars" && <FunctionField label="Typ" render={_renderGearType} source="gearType" />}
      {resource === "simulators" ? (
        <ReferenceField label="Filiale" source="branchId" reference="branches" />
      ) : (
        !isDeleted && (
          <ReferenceArrayField label="Fahrlehrer" reference="instructors" source="entitledInstructorIds">
            <CommaSeparatedFieldList maxItems={1}>
              <ReferenceField source="id" reference="instructors" className="whitespace-nowrap" />
            </CommaSeparatedFieldList>
          </ReferenceArrayField>
        )
      )}
      {isDeleted && <DateField label="Gelöscht am" source="deletedAt" showDate="with diff" />}
    </Datagrid>
  );
}

function _getRowClick(resource: VehicleListResource, variant: VehicleListVariant): "edit" | false {
  if (variant === "deleted") {
    return "edit";
  }
  if (resource === "simulators") {
    return grants.has("Vehicle:edit") || grants.has("Vehicle:show") ? "edit" : false;
  }
  return grants.has("Vehicle:edit") ? "edit" : false;
}

function _renderGearType(car: Car) {
  return formatGearType(car.gearType);
}
